import React, { useState, useEffect, useRef, useMemo, useLayoutEffect } from 'react';
import { collection, getDocs, Firestore, doc, updateDoc, setDoc } from 'firebase/firestore';
import { dbTools, auth } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import { ViewMode } from './Login';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown, faClose, faLink, faCopy, faCaretLeft, faCaretRight, faChevronRight, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { setNBMTMFavicon, restoreOriginalFavicon } from '../../utils/favicon';
import '../../App.css';
import './nbmtm.css';
import { DeleteWarningPopup, ConditionalPopup } from './components/Popup';
import AutoSizingTextarea from './components/AutoSizingTextarea';

interface NBMTMField {
  id: string;
  type: string;
  value: string;
  inputValue?: string;
  options?: string[];
  selectedOption?: string | null;
  conditionalField?: string | null;
  conditionalValue?: string | null;
}

interface NBMTMItem {
  id: string;
  name: string;
  subtitle?: string;
  fields?: NBMTMField[];
}

interface NBMTMDocument {
  id: string;
  items?: NBMTMItem[];
  [key: string]: any;
}

interface FieldPosition {
  id: string;
  y: number;
}

function Secure() {
  const [documents, setDocuments] = useState<NBMTMDocument[]>([]);
  const [selectedDocId, setSelectedDocId] = useState<string>('');
  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newItemName, setNewItemName] = useState('');
  const [fieldType, setFieldType] = useState('');
  const [fieldPositions, setFieldPositions] = useState<FieldPosition[]>([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [deleteWarning, setDeleteWarning] = useState<{itemId: string, itemName: string, fieldCount: number} | null>(null);
  const [viewMode] = useState<ViewMode>(() => 
    (sessionStorage.getItem('nbmtm_view_mode') as ViewMode) || 'VIEWING'
  );
  const fieldsContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [animatingFields, setAnimatingFields] = useState<{[key: string]: { transform: string, class: string }}>({});
  const [pendingSwap, setPendingSwap] = useState<{
    fieldIds: string[],
    newFields: NBMTMField[],
    completedTransitions: number
  } | null>(null);
  const [pendingItemSwap, setPendingItemSwap] = useState<{
    itemIds: string[],
    newItems: NBMTMItem[],
    completedTransitions: number
  } | null>(null);
  const [animatingItems, setAnimatingItems] = useState<{[key: string]: { transform: string, class: string }}>({});
  const [hoveredFieldId, setHoveredFieldId] = useState<string | null>(null);
  const [hoveredButtonId, setHoveredButtonId] = useState<string | null>(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [textareaRefs] = useState<{[key: string]: React.RefObject<HTMLTextAreaElement>}>({});
  const [conditionalPopup, setConditionalPopup] = useState<{
    fieldId: string,
    isConditional: boolean
  } | null>(null);
  const [showAutosave, setShowAutosave] = useState(false);
  const [pendingOptionSwap, setPendingOptionSwap] = useState<{
    fieldId: string,
    optionIndexes: number[],
    newOptions: string[],
    completedTransitions: number
  } | null>(null);
  const [animatingOptions, setAnimatingOptions] = useState<{[key: string]: { transform: string, class: string }}>({});
  const isManualSelection = useRef(false);

  useEffect(() => {
    document.title = 'NBMTM Operations';
    setNBMTMFavicon();
    return () => {
      restoreOriginalFavicon();
    };
  }, []);

  // Add helper function to handle autosave indicator
  const triggerAutosave = () => {
    setShowAutosave(true);
    setTimeout(() => {
      setShowAutosave(false);
    }, 2000);
  };

  // Add helper function to check if a field should be visible
  const isFieldVisible = (field: NBMTMField, fields: NBMTMField[]): boolean => {
    if (viewMode === 'AUTHORING') return true;
    if (!field.conditionalField) return true;
    
    const controllingField = fields.find(f => f.id === field.conditionalField);
    if (!controllingField) return true;
    
    return controllingField.selectedOption === field.conditionalValue;
  };

  // Add helper function to check field incompleteness
  const isFieldIncomplete = (field: NBMTMField, fields: NBMTMField[]): boolean => {
    if (!isFieldVisible(field, fields)) return false;
    if (field.type === 'comment') return false;
    
    return ((field.type === 'shortanswer' || field.type === 'longanswer') && !field.inputValue) ||
           (field.type === 'options' && field.selectedOption === null);
  };

  const isAuthoringFieldIncomplete = (field: NBMTMField): boolean => {
    if (field.type === 'comment') {
      return field.value === '' || field.value === 'Comment...';
    }
    return field.value === '' || field.value === 'Question...';
  };

  useEffect(() => {
    // Check both session storage and Firebase auth
    const isAuthenticated = sessionStorage.getItem('nbmtm_authenticated') === 'true';
    if (!isAuthenticated || !auth) {
      navigate('/nbmtmops');
      return;
    }

    let mounted = true;
    
    const loadDocuments = async () => {
      try {
        console.log('Loading nbmtmform documents...');
        const colRef = collection(dbTools as Firestore, 'nbmtmform');
        const snapshot = await getDocs(colRef);
        
        if (mounted) {
          const docs = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              items: Array.isArray(data.items) ? data.items : []
            };
          });

          console.log('Documents loaded:', docs);
          setDocuments(docs);
          if (docs.length > 0) {
            setSelectedDocId(docs[0].id);
            // Select the first item if available
            if (docs[0].items && docs[0].items.length > 0) {
              setSelectedItemId(docs[0].items[0].id);
            }
          }
          setError(null);
        }
      } catch (error) {
        if (mounted) {
          console.error('Error loading documents:', error);
          setError('Error loading documents. Please try again later.');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadDocuments();
    
    return () => {
      mounted = false;
    };
  }, [navigate]);

  const selectedDocument = documents.find(doc => doc.id === selectedDocId);

  const handleAddItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDocument || !newItemName.trim()) return;

    try {
      const docRef = doc(dbTools as Firestore, 'nbmtmform', selectedDocument.id);
      const newItem: NBMTMItem = {
        id: Date.now().toString(), // Simple unique ID
        name: newItemName.trim()
      };

      const updatedItems = [...(selectedDocument.items || []), newItem];
      await updateDoc(docRef, { items: updatedItems });

      // Update local state
      setDocuments(docs => docs.map(doc => 
        doc.id === selectedDocument.id 
          ? { ...doc, items: updatedItems }
          : doc
      ));

      // Clear form
      setNewItemName('');
      
      // Select the newly added item
      setSelectedItemId(newItem.id);
    } catch (error) {
      console.error('Error adding item:', error);
      setError('Error adding item. Please try again.');
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!selectedDocument) return;

    try {
      const docRef = doc(dbTools as Firestore, 'nbmtmform', selectedDocument.id);
      const items = selectedDocument.items || [];
      const currentIndex = items.findIndex(item => item.id === itemId);
      const updatedItems = items.filter(item => item.id !== itemId);
      
      await updateDoc(docRef, { items: updatedItems });

      // Update local state
      setDocuments(docs => docs.map(doc => 
        doc.id === selectedDocument.id 
          ? { ...doc, items: updatedItems }
          : doc
      ));
      
      // Select the next closest item
      if (updatedItems.length > 0) {
        if (currentIndex < items.length - 1) {
          // If there was an item after this one, select it
          setSelectedItemId(items[currentIndex + 1].id);
        } else if (currentIndex > 0) {
          // Otherwise if there was an item before this one, select it
          setSelectedItemId(items[currentIndex - 1].id);
        } else {
          // If neither, select the first item
          setSelectedItemId(updatedItems[0].id);
        }
      } else {
        setSelectedItemId('');
      }
      
      setDeleteWarning(null);
    } catch (error) {
      console.error('Error deleting item:', error);
      setError('Error deleting item. Please try again.');
    }
  };

  // Add this function to calculate and update field positions
  const updateFieldPositions = () => {
    if (!fieldsContainerRef.current) return;
    
    const newPositions: FieldPosition[] = [];
    const fieldElements = fieldsContainerRef.current.querySelectorAll('.field-container');
    
    fieldElements.forEach((element) => {
      const fieldId = element.getAttribute('data-field-id');
      if (fieldId) {
        const rect = element.getBoundingClientRect();
        const containerRect = fieldsContainerRef.current!.getBoundingClientRect();
        newPositions.push({
          id: fieldId,
          y: rect.top - containerRect.top
        });
      }
    });
    
    setFieldPositions(newPositions);
  };

  useEffect(() => {
    updateFieldPositions();
  }, [selectedDocument?.items?.find(item => item.id === selectedItemId)?.fields]);

  // Function to get the previous position of a field
  const getPreviousPosition = (fieldId: string) => {
    const position = fieldPositions.find(pos => pos.id === fieldId);
    return position ? position.y : 0;
  };

  // Memoize the fields to prevent unnecessary re-renders
  const fields = useMemo(() => {
    return selectedDocument?.items?.find(item => item.id === selectedItemId)?.fields || [];
  }, [selectedDocument, selectedItemId]);

  const handleTransitionEnd = async (fieldId: string) => {
    if (!pendingSwap || !pendingSwap.fieldIds.includes(fieldId)) return;

    const newCompleted = pendingSwap.completedTransitions + 1;
    
    if (newCompleted === 2) {
      // Update the database first
      const docRef = doc(dbTools as Firestore, 'nbmtmform', selectedDocument!.id);
      const updatedItems = selectedDocument!.items?.map(item =>
        item.id === selectedItemId
          ? { ...item, fields: pendingSwap.newFields }
          : item
      ) || [];

      await updateDoc(docRef, { items: updatedItems });
      
      // Clear animation states
      setAnimatingFields({});
      setPendingSwap(null);
      setIsAnimating(false);

      // Update local state last
      setDocuments(docs => docs.map(doc => 
        doc.id === selectedDocument!.id 
          ? { ...doc, items: updatedItems }
          : doc
      ));
    } else {
      setPendingSwap(prev => prev ? { ...prev, completedTransitions: newCompleted } : null);
    }
  };

  const handleItemTransitionEnd = async (itemId: string) => {
    if (!pendingItemSwap || !pendingItemSwap.itemIds.includes(itemId)) return;

    const newCompleted = pendingItemSwap.completedTransitions + 1;
    
    if (newCompleted === 2) {
      // Update the database first
      const docRef = doc(dbTools as Firestore, 'nbmtmform', selectedDocument!.id);
      await updateDoc(docRef, { items: pendingItemSwap.newItems });
      
      // Clear animation states
      setAnimatingItems({});
      setPendingItemSwap(null);
      setIsAnimating(false);

      // Update local state last
      setDocuments(docs => docs.map(doc => 
        doc.id === selectedDocument!.id 
          ? { ...doc, items: pendingItemSwap.newItems }
          : doc
      ));
    } else {
      setPendingItemSwap(prev => prev ? { ...prev, completedTransitions: newCompleted } : null);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update click handler for items to set manual selection
  const handleItemClick = (itemId: string) => {
    isManualSelection.current = true;
    setSelectedItemId(itemId);
    if (windowWidth <= 768) {
      setIsSidebarOpen(false);
    }
  };

  // Auto-collapse sidebar on mobile when no item is selected
  useEffect(() => {
    if (windowWidth <= 768) {
      if (!selectedItemId) {
        setIsSidebarOpen(true);
      } else if (isManualSelection.current) {
        setIsSidebarOpen(false);
      }
    } else {
      setIsSidebarOpen(true);
    }
  }, [selectedItemId, windowWidth]);

  // Reset manual selection flag when documents change
  useEffect(() => {
    isManualSelection.current = false;
  }, [documents]);

  // Function to adjust textarea height
  const adjustTextareaHeight = (textarea: HTMLTextAreaElement | null) => {
    if (textarea) {
      // Reset to default height
      textarea.style.height = '1.5em';
      // Get the scrollHeight
      const scrollHeight = textarea.scrollHeight;
      // Only adjust if content needs more space
      if (scrollHeight > textarea.offsetHeight) {
        textarea.style.height = `${scrollHeight}px`;
      }
    }
  };

  // Function to adjust field label height
  const adjustFieldLabelHeight = (label: HTMLDivElement | null) => {
    if (label) {
      // Reset to default height
      label.style.height = '1.5em';
      // Get the scrollHeight
      const scrollHeight = label.scrollHeight;
      // Only adjust if content needs more space
      if (scrollHeight > label.offsetHeight) {
        label.style.height = `${scrollHeight}px`;
      }
    }
  };

  // Effect to handle initial and value change height adjustments
  useEffect(() => {
    fields.forEach(field => {
      if (field.type === 'comment' || field.type === 'shortanswer' || field.type === 'longanswer') {
        const textarea = document.querySelector(`[data-field-id="${field.id}"] textarea`) as HTMLTextAreaElement;
        const label = document.querySelector(`[data-field-id="${field.id}"] .field-label`) as HTMLDivElement;
        adjustTextareaHeight(textarea);
        adjustFieldLabelHeight(label);
      }
    });
  }, [fields, selectedItemId]);

  const handleOptionTransitionEnd = async (fieldId: string, optionIndex: number) => {
    if (!pendingOptionSwap || pendingOptionSwap.fieldId !== fieldId || !pendingOptionSwap.optionIndexes.includes(optionIndex)) return;

    const newCompleted = pendingOptionSwap.completedTransitions + 1;
    
    if (newCompleted === 2) {
      // Update the database first
      const docRef = doc(dbTools as Firestore, 'nbmtmform', selectedDocument!.id);
      const updatedItems = selectedDocument!.items?.map(item =>
        item.id === selectedItemId
          ? {
              ...item,
              fields: item.fields?.map(f =>
                f.id === fieldId
                  ? {
                      ...f,
                      options: pendingOptionSwap.newOptions,
                      selectedOption: f.selectedOption
                    }
                  : f
              ) || []
            }
          : item
      ) || [];

      await updateDoc(docRef, { items: updatedItems });
      
      // Clear animation states
      setAnimatingOptions({});
      setPendingOptionSwap(null);

      // Update local state last
      setDocuments(docs => docs.map(doc => 
        doc.id === selectedDocument!.id 
          ? { ...doc, items: updatedItems }
          : doc
      ));

      triggerAutosave();
    } else {
      setPendingOptionSwap(prev => prev ? { ...prev, completedTransitions: newCompleted } : null);
    }
  };

  // Add helper functions for navigation
  const getNextItemInfo = () => {
    if (!documents.length) return null;

    const currentDocIndex = documents.findIndex(doc => doc.id === selectedDocId);
    const currentDoc = documents[currentDocIndex];
    const currentItemIndex = currentDoc?.items?.findIndex(item => item.id === selectedItemId) ?? -1;

    // Check next item in current collection
    if (currentDoc?.items && currentItemIndex < currentDoc.items.length - 1) {
      return {
        docId: currentDoc.id,
        itemId: currentDoc.items[currentItemIndex + 1].id
      };
    }

    // Look for items in next collection
    let nextDocIndex = (currentDocIndex + 1) % documents.length;
    while (nextDocIndex !== currentDocIndex) {
      const nextDoc = documents[nextDocIndex];
      if (nextDoc?.items?.length) {
        return {
          docId: nextDoc.id,
          itemId: nextDoc.items[0].id
        };
      }
      nextDocIndex = (nextDocIndex + 1) % documents.length;
    }

    // If we're back to the current collection, return first item
    if (currentDoc?.items?.length) {
      return {
        docId: currentDoc.id,
        itemId: currentDoc.items[0].id
      };
    }

    return null;
  };

  const getPrevItemInfo = () => {
    if (!documents.length) return null;

    const currentDocIndex = documents.findIndex(doc => doc.id === selectedDocId);
    const currentDoc = documents[currentDocIndex];
    const currentItemIndex = currentDoc?.items?.findIndex(item => item.id === selectedItemId) ?? -1;

    // Check previous item in current collection
    if (currentDoc?.items && currentItemIndex > 0) {
      return {
        docId: currentDoc.id,
        itemId: currentDoc.items[currentItemIndex - 1].id
      };
    }

    // Look for items in previous collection
    let prevDocIndex = (currentDocIndex - 1 + documents.length) % documents.length;
    while (prevDocIndex !== currentDocIndex) {
      const prevDoc = documents[prevDocIndex];
      if (prevDoc?.items?.length) {
        return {
          docId: prevDoc.id,
          itemId: prevDoc.items[prevDoc.items.length - 1].id
        };
      }
      prevDocIndex = (prevDocIndex - 1 + documents.length) % documents.length;
    }

    // If we're back to the current collection, return last item
    if (currentDoc?.items?.length) {
      return {
        docId: currentDoc.id,
        itemId: currentDoc.items[currentDoc.items.length - 1].id
      };
    }

    return null;
  };

  // Calculate total number of items across all collections
  const getTotalItems = () => {
    return documents.reduce((total, doc) => total + (doc.items?.length || 0), 0);
  };

  if (loading) return (
    <div className="App">
      <header className="App-header">
        <p>Loading nbmtmform documents...</p>
        <p style={{ fontSize: '0.8rem', color: '#666' }}>
          Check console for loading details (Press F12 and select Console)
        </p>
      </header>
    </div>
  );
  
  if (error) return (
    <div className="App">
      <header className="App-header">
        <h2>Error Loading Documents</h2>
        <p>{error}</p>
        <p style={{ fontSize: '0.8rem', color: '#666' }}>
          Check console for error details (Press F12 and select Console)
        </p>
      </header>
    </div>
  );

  return (
    <div className="nbmtm-secure" data-viewmode={viewMode}>
      {deleteWarning && (
        <DeleteWarningPopup
          itemName={deleteWarning.itemName}
          fieldCount={deleteWarning.fieldCount}
          onCancel={() => setDeleteWarning(null)}
          onDelete={() => handleDeleteItem(deleteWarning.itemId)}
        />
      )}
      
      {conditionalPopup && (
        <ConditionalPopup
          fields={selectedDocument?.items?.find(item => item.id === selectedItemId)?.fields || []}
          selectedItemId={selectedItemId}
          fieldId={conditionalPopup.fieldId}
          isConditional={conditionalPopup.isConditional}
          selectedField={selectedDocument?.items?.find(item => 
            item.id === selectedItemId
          )?.fields?.find(f => 
            f.id === conditionalPopup.fieldId
          )}
          onCancel={() => setConditionalPopup(null)}
          onSave={(fieldId, conditionalField, conditionalValue) => {
            const docRef = doc(dbTools as Firestore, 'nbmtmform', selectedDocument!.id);
            const updatedItems = selectedDocument!.items?.map(item =>
              item.id === selectedItemId
                ? {
                    ...item,
                    fields: item.fields?.map(f =>
                      f.id === fieldId
                        ? { 
                            ...f, 
                            conditionalField,
                            conditionalValue
                          }
                        : f
                    ) || []
                  }
                : item
            ) || [];
            updateDoc(docRef, { items: updatedItems }).then(() => {
              triggerAutosave();
            });
            setDocuments(docs => docs.map(doc => 
              doc.id === selectedDocument!.id 
                ? { ...doc, items: updatedItems }
                : doc
            ));
            setConditionalPopup(null);
          }}
        />
      )}
      
      {/* Mobile Toggle Button */}
      <button
        className={`toggle-button ${isSidebarOpen ? 'sidebar-open' : ''}`}
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        style={{
          display: windowWidth <= 768 && !isSidebarOpen ? 'block' : 'none'
        }}
      >
        {isSidebarOpen ? <FontAwesomeIcon icon={faChevronLeft} /> : <FontAwesomeIcon icon={faChevronRight} />}
      </button>

      {/* Left Navigation */}
      <div className={`nbmtm-secure-sidebar ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        {/* Fixed Collection Dropdown */}
        <div className="nbmtm-secure-collection">
          <p className="nbmtm-secure-collection-title">NBMTM Operations</p>
          {viewMode === 'AUTHORING' && (
            <p className="nbmtm-secure-collection-subtitle">Authoring</p>
          )}
          {viewMode === 'FILLINGOUT' && (
            <p className="nbmtm-secure-collection-subtitle">Entry</p>
          )}
          <select
            className="nbmtm-secure-select"
            value={selectedDocId}
            onChange={(e) => {
              const newDocId = e.target.value;
              setSelectedDocId(newDocId);
              // Automatically select the first item in the new collection
              const newDoc = documents.find(doc => doc.id === newDocId);
              const items = newDoc?.items;
              if (items && items.length > 0) {
                setSelectedItemId(items[0].id);
              }
            }}
            style={{
              borderColor: viewMode === 'AUTHORING' && selectedDocument?.items?.some(item => 
                item.fields?.some(field => isAuthoringFieldIncomplete(field))
              ) ? '#ffeb3b30' : 
              viewMode !== 'AUTHORING' && selectedDocument?.items?.some(item => 
                item.fields?.some(field => 
                  (field.type === 'comment' && !field.value) || 
                  ((field.type === 'shortanswer' || field.type === 'longanswer') && !field.inputValue) ||
                  (field.type === 'options' && field.selectedOption === null)
                )
              ) ? '#ff6b6b' : '#ffffff30'
            }}
          >
            {documents.map(doc => (
              <option key={doc.id} value={doc.id}>
                {doc.id}
              </option>
            ))}
          </select>

          {viewMode === 'AUTHORING' && selectedDocument && (
            <form onSubmit={handleAddItem}>
              <input
                type="text"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder="New item..."
                className="nbmtm-secure-input"
              />
            </form>
          )}
        </div>

        {/* Scrollable Content */}
        <div className="nbmtm-secure-items">
          {selectedDocument && (
            <div>
              {(selectedDocument.items || []).map((item) => (
                <div
                  key={item.id}
                  data-item-id={item.id}
                  className={`nbmtm-secure-item ${selectedItemId === item.id ? 'selected' : ''} ${animatingItems[item.id]?.class || ''}`}
                  onClick={() => handleItemClick(item.id)}
                  onTransitionEnd={() => handleItemTransitionEnd(item.id)}
                  style={{
                    transform: animatingItems[item.id]?.transform || ''
                  }}
                >
                  <span style={{ 
                    color: viewMode === 'AUTHORING' ? 
                      (item.fields?.some(field => isAuthoringFieldIncomplete(field)) ? '#ffeb3b' : 'white') :
                      viewMode === 'FILLINGOUT' ? 
                        (item.fields?.some(field => isFieldIncomplete(field, item.fields || [])) ? '#ff6b6b' : 'white') :
                        (item.fields?.some(field => isFieldIncomplete(field, item.fields || [])) ? '#ff6b6b' : 'white')
                  }}>{item.name}</span>
                  {viewMode === 'AUTHORING' && (
                    <div className="item-controls">
                                          <button
                        onMouseEnter={() => setHoveredButtonId(`${item.id}-duplicate`)}
                        onMouseLeave={() => setHoveredButtonId(null)}
                        onClick={async (e) => {
                          e.stopPropagation();
                          const items = selectedDocument.items || [];
                          const currentIndex = items.findIndex(i => i.id === item.id);
                          
                          // Create a deep copy of the current item with a new ID
                          const newItem = {
                            ...JSON.parse(JSON.stringify(item)),
                            id: Date.now().toString(),
                            name: `${item.name} copy`
                          };
                          
                          // Insert the new item right after the current item
                          const newItems = [
                            ...items.slice(0, currentIndex + 1),
                            newItem,
                            ...items.slice(currentIndex + 1)
                          ];
                          
                          // Update the database
                          const docRef = doc(dbTools as Firestore, 'nbmtmform', selectedDocument!.id);
                          await updateDoc(docRef, { items: newItems });
                          
                          // Update local state
                          setDocuments(docs => docs.map(doc => 
                            doc.id === selectedDocument!.id 
                              ? { ...doc, items: newItems }
                              : doc
                          ));
                          
                          // Select the newly duplicated item
                          setSelectedItemId(newItem.id);
                          
                          triggerAutosave();
                        }}
                      >
                        <FontAwesomeIcon icon={faCopy} />
                      </button>
                      <button
                        onMouseEnter={() => setHoveredButtonId(`${item.id}-up`)}
                        onMouseLeave={() => setHoveredButtonId(null)}
                        style={{
                          opacity: selectedDocument.items?.findIndex(i => i.id === item.id) === 0 ? '0.5' : '1',
                          pointerEvents: selectedDocument.items?.findIndex(i => i.id === item.id) === 0 ? 'none' : 'auto'
                        }}
                        onClick={async (e) => {
                          e.stopPropagation();
                          const items = selectedDocument.items || [];
                          const currentIndex = items.findIndex(i => i.id === item.id);
                          
                          if (currentIndex > 0) {
                            setIsAnimating(true);
                            
                            const newItems = [...items];
                            [newItems[currentIndex - 1], newItems[currentIndex]] = 
                              [newItems[currentIndex], newItems[currentIndex - 1]];
                            
                            const currentItem = document.querySelector(`[data-item-id="${item.id}"]`) as HTMLElement;
                            const prevItem = document.querySelector(`[data-item-id="${items[currentIndex - 1].id}"]`) as HTMLElement;
                            
                            if (currentItem && prevItem) {
                              const currentItemHeight = currentItem.offsetHeight;
                              const prevItemHeight = prevItem.offsetHeight;
                              const gap = 4;
                              const distanceUp = prevItemHeight + gap;
                              const distanceDown = currentItemHeight + gap;

                              setPendingItemSwap({
                                itemIds: [item.id, items[currentIndex - 1].id],
                                newItems,
                                completedTransitions: 0
                              });
                              
                              setAnimatingItems({
                                [item.id]: { 
                                  transform: `translateY(${-distanceUp}px)`,
                                  class: 'animating'
                                },
                                [items[currentIndex - 1].id]: { 
                                  transform: `translateY(${distanceDown}px)`,
                                  class: 'animating'
                                }
                              });
                            }
                          }
                        }}
                      >
                        <FontAwesomeIcon icon={faChevronUp} />
                      </button>
                      <button
                        onMouseEnter={() => setHoveredButtonId(`${item.id}-down`)}
                        onMouseLeave={() => setHoveredButtonId(null)}
                        style={{
                          opacity: selectedDocument.items?.findIndex(i => i.id === item.id) === (selectedDocument.items?.length || 0) - 1 ? '0.5' : '1',
                          pointerEvents: selectedDocument.items?.findIndex(i => i.id === item.id) === (selectedDocument.items?.length || 0) - 1 ? 'none' : 'auto'
                        }}
                        onClick={async (e) => {
                          e.stopPropagation();
                          const items = selectedDocument.items || [];
                          const currentIndex = items.findIndex(i => i.id === item.id);
                          
                          if (currentIndex < items.length - 1) {
                            setIsAnimating(true);
                            
                            const newItems = [...items];
                            [newItems[currentIndex], newItems[currentIndex + 1]] = 
                              [newItems[currentIndex + 1], newItems[currentIndex]];
                            
                            const currentItem = document.querySelector(`[data-item-id="${item.id}"]`) as HTMLElement;
                            const nextItem = document.querySelector(`[data-item-id="${items[currentIndex + 1].id}"]`) as HTMLElement;
                            
                            if (currentItem && nextItem) {
                              const currentItemHeight = currentItem.offsetHeight;
                              const nextItemHeight = nextItem.offsetHeight;
                              const gap = 4;
                              const distanceDown = nextItemHeight + gap;
                              const distanceUp = currentItemHeight + gap;

                              setPendingItemSwap({
                                itemIds: [item.id, items[currentIndex + 1].id],
                                newItems,
                                completedTransitions: 0
                              });
                              
                              setAnimatingItems({
                                [item.id]: { 
                                  transform: `translateY(${distanceDown}px)`,
                                  class: 'animating'
                                },
                                [items[currentIndex + 1].id]: { 
                                  transform: `translateY(${-distanceUp}px)`,
                                  class: 'animating'
                                }
                              });
                            }
                          }
                        }}
                      >
                        <FontAwesomeIcon icon={faChevronDown} />
                      </button>
                      <button
                        className="nbmtm-secure-item-delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          const itemFields = selectedDocument.items?.find(i => i.id === item.id)?.fields || [];
                          if (itemFields.length > 0) {
                            setDeleteWarning({
                              itemId: item.id,
                              itemName: item.name,
                              fieldCount: itemFields.length
                            });
                          } else {
                            handleDeleteItem(item.id);
                          }
                        }}
                        onMouseEnter={() => setHoveredButtonId(`${item.id}-delete`)}
                        onMouseLeave={() => setHoveredButtonId(null)}
                      >
                        <FontAwesomeIcon icon={faClose} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div 
        className={`nbmtm-secure-content ${isSidebarOpen && windowWidth <= 768 ? 'content-area-with-sidebar' : ''}`}
        onClick={(e) => {
          if (windowWidth <= 768 && isSidebarOpen) {
            setIsSidebarOpen(false);
          }
        }}
      >
        {documents.length === 0 ? (
          <div className="console-container">
            <p>No documents found in nbmtmform collection</p>
            <p className="console-details">
              Check console for connection details (Press F12 and select Console)
            </p>
          </div>
        ) : selectedDocument && selectedItemId && (selectedDocument.items || []).length > 0 && (
          <>
            {/* Fixed Header */}
            <div className="nbmtm-secure-header">
              <div className="nbmtm-secure-header-container" style={{ position: 'relative' }}>
                <div className={`autosave-indicator ${!showAutosave ? 'hidden' : ''}`}>
                  Autosaved
                </div>
                <div className="header-nav-container left">
                  <button
                    onClick={() => {
                      const currentDocIndex = documents.findIndex(doc => doc.id === selectedDocId);
                      const currentDoc = documents[currentDocIndex];
                      const currentItemIndex = currentDoc?.items?.findIndex(item => item.id === selectedItemId) ?? -1;

                      // Check previous item in current collection
                      if (currentDoc?.items && currentItemIndex > 0) {
                        setSelectedItemId(currentDoc.items[currentItemIndex - 1].id);
                        return;
                      }

                      // Look for items in previous collection
                      let prevDocIndex = (currentDocIndex - 1 + documents.length) % documents.length;
                      while (prevDocIndex !== currentDocIndex) {
                        const prevDoc = documents[prevDocIndex];
                        if (prevDoc?.items?.length) {
                          setSelectedDocId(prevDoc.id);
                          setSelectedItemId(prevDoc.items[prevDoc.items.length - 1].id);
                          return;
                        }
                        prevDocIndex = (prevDocIndex - 1 + documents.length) % documents.length;
                      }

                      // If we're back to the current collection, go to last item
                      if (currentDoc?.items?.length) {
                        setSelectedItemId(currentDoc.items[currentDoc.items.length - 1].id);
                      }
                    }}
                    className={`nbmtm-secure-nav-button ${documents.reduce((total, doc) => total + (doc.items?.length || 0), 0) <= 1 ? 'disabled' : ''}`}
                  >
                    <FontAwesomeIcon icon={faCaretLeft} />
                  </button>
                </div>
                <input
                  type="text"
                  value={selectedDocument?.items?.find(item => item.id === selectedItemId)?.name || ''}
                  onChange={(e) => {
                    if (viewMode === 'AUTHORING') {
                      const docRef = doc(dbTools as Firestore, 'nbmtmform', selectedDocument.id);
                      const updatedItems = selectedDocument.items?.map(item =>
                        item.id === selectedItemId
                          ? { ...item, name: e.target.value }
                          : item
                      ) || [];
                      updateDoc(docRef, { items: updatedItems }).then(() => {
                        triggerAutosave();
                      });
                      setDocuments(docs => docs.map(doc => 
                        doc.id === selectedDocument.id 
                          ? { ...doc, items: updatedItems }
                          : doc
                      ));
                    }
                  }}
                  readOnly={viewMode !== 'AUTHORING'}
                  className={`nbmtm-secure-title-input ${
                    viewMode === 'AUTHORING' ? 
                      (selectedDocument?.items?.find(item => item.id === selectedItemId)?.fields?.some(field => 
                        isAuthoringFieldIncomplete(field)
                      ) ? 'authoring-incomplete' : 'authoring') :
                    viewMode === 'FILLINGOUT' ? 
                      (selectedDocument.items?.find(item => item.id === selectedItemId)?.fields?.some(field => 
                        isFieldIncomplete(field, selectedDocument.items?.find(item => item.id === selectedItemId)?.fields || [])
                      ) ? 'incomplete' : 'filling') :
                      (selectedDocument.items?.find(item => item.id === selectedItemId)?.fields?.some(field => 
                        isFieldIncomplete(field, selectedDocument.items?.find(item => item.id === selectedItemId)?.fields || [])
                      ) ? 'incomplete' : 'viewing')
                  }`}
                />
                {(viewMode === 'AUTHORING' || (selectedDocument.items?.find(item => item.id === selectedItemId)?.subtitle)) && (
                  <input
                    type="text"
                    value={selectedDocument.items?.find(item => item.id === selectedItemId)?.subtitle || ''}
                    onChange={(e) => {
                      if (viewMode === 'AUTHORING') {
                        const docRef = doc(dbTools as Firestore, 'nbmtmform', selectedDocument.id);
                        const updatedItems = selectedDocument.items?.map(item =>
                          item.id === selectedItemId
                            ? { ...item, subtitle: e.target.value }
                            : item
                        ) || [];
                        updateDoc(docRef, { items: updatedItems }).then(() => {
                          triggerAutosave();
                        });
                        setDocuments(docs => docs.map(doc => 
                          doc.id === selectedDocument.id 
                            ? { ...doc, items: updatedItems }
                            : doc
                        ));
                      }
                    }}
                    readOnly={viewMode !== 'AUTHORING'}
                    placeholder="Add description..."
                    className={`nbmtm-secure-subtitle-input ${
                      viewMode === 'AUTHORING' ? 'authoring' : 'viewing'
                    } ${
                      viewMode === 'AUTHORING' || selectedDocument.items?.find(item => item.id === selectedItemId)?.subtitle ? 'visible' : 'hidden'
                    }`}
                  />
                )}
                <div className="header-nav-container right">
                  <button
                    onClick={() => {
                      const currentDocIndex = documents.findIndex(doc => doc.id === selectedDocId);
                      const currentDoc = documents[currentDocIndex];
                      const currentItemIndex = currentDoc?.items?.findIndex(item => item.id === selectedItemId) ?? -1;

                      // Check next item in current collection
                      if (currentDoc?.items && currentItemIndex < currentDoc.items.length - 1) {
                        setSelectedItemId(currentDoc.items[currentItemIndex + 1].id);
                        return;
                      }

                      // Look for items in next collection
                      let nextDocIndex = (currentDocIndex + 1) % documents.length;
                      while (nextDocIndex !== currentDocIndex) {
                        const nextDoc = documents[nextDocIndex];
                        if (nextDoc?.items?.length) {
                          setSelectedDocId(nextDoc.id);
                          setSelectedItemId(nextDoc.items[0].id);
                          return;
                        }
                        nextDocIndex = (nextDocIndex + 1) % documents.length;
                      }

                      // If we're back to the current collection, go to first item
                      if (currentDoc?.items?.length) {
                        setSelectedItemId(currentDoc.items[0].id);
                      }
                    }}
                    className={`nbmtm-secure-nav-button ${documents.reduce((total, doc) => total + (doc.items?.length || 0), 0) <= 1 ? 'disabled' : ''}`}
                  >
                    <FontAwesomeIcon icon={faCaretRight} />
                  </button>
                </div>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="nbmtm-secure-fields">
              <div ref={fieldsContainerRef} className="fields-container">
                {fields.map(field => (
                  isFieldVisible(field, fields) ? (
                    <div
                      key={field.id}
                      data-field-id={field.id}
                      className={`field-container ${viewMode !== 'AUTHORING' ? 'viewing' : ''} ${animatingFields[field.id]?.class || ''}`}
                      onTransitionEnd={() => handleTransitionEnd(field.id)}
                      onMouseEnter={() => setHoveredFieldId(field.id)}
                      onMouseLeave={() => setHoveredFieldId(null)}
                      style={{
                        transform: animatingFields[field.id]?.transform || ''
                      }}>
                      {viewMode === 'AUTHORING' && (
                        <>
                          <button
                            className="field-delete-button"
                            onMouseEnter={() => setHoveredButtonId(`${field.id}-delete`)}
                            onMouseLeave={() => setHoveredButtonId(null)}
                            onClick={() => {
                              const docRef = doc(dbTools as Firestore, 'nbmtmform', selectedDocument.id);
                              const updatedItems = selectedDocument.items?.map(item =>
                                item.id === selectedItemId
                                  ? {
                                      ...item,
                                      fields: item.fields?.filter(f => f.id !== field.id) || []
                                    }
                                  : item
                              ) || [];
                              updateDoc(docRef, { items: updatedItems }).then(() => {
                                triggerAutosave();
                              });
                              setDocuments(docs => docs.map(doc => 
                                doc.id === selectedDocument.id 
                                  ? { ...doc, items: updatedItems }
                                  : doc
                              ));
                            }}
                          >
                            <FontAwesomeIcon icon={faClose} />
                          </button>
                          <div className="field-order-buttons">
                            {(field.type !== 'options' || (selectedDocument?.items?.find(item => item.id === selectedItemId)?.fields || []).filter(f => f.type === 'options').length > 1) && (
                              <button
                                className="conditional-button"
                                onMouseEnter={() => setHoveredButtonId(`${field.id}-circle`)}
                                onMouseLeave={() => setHoveredButtonId(null)}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setConditionalPopup({
                                    fieldId: field.id,
                                    isConditional: !!field.conditionalField
                                  });
                                }}
                              >
                                <FontAwesomeIcon icon={faLink} className={`conditional-indicator ${field.conditionalField ? 'active' : 'inactive'}`} />
                              </button>
                            )}
                            <button
                              className={`order-button ${selectedDocument.items?.find(item => item.id === selectedItemId)?.fields?.findIndex(f => f.id === field.id) === 0 ? 'disabled' : ''}`}
                              onMouseEnter={() => setHoveredButtonId(`${field.id}-up`)}
                              onMouseLeave={() => setHoveredButtonId(null)}
                              onClick={async () => {
                                const docRef = doc(dbTools as Firestore, 'nbmtmform', selectedDocument.id);
                                const currentItem = selectedDocument.items?.find(item => item.id === selectedItemId);
                                const fields = currentItem?.fields || [];
                                const currentIndex = fields.findIndex(f => f.id === field.id);
                                
                                if (currentIndex > 0) {
                                  setIsAnimating(true);
                                  
                                  const newFields = [...fields];
                                  [newFields[currentIndex - 1], newFields[currentIndex]] = 
                                    [newFields[currentIndex], newFields[currentIndex - 1]];
                                  
                                  const currentField = fieldsContainerRef.current?.querySelector(`[data-field-id="${field.id}"]`) as HTMLElement;
                                  const prevField = fieldsContainerRef.current?.querySelector(`[data-field-id="${fields[currentIndex - 1].id}"]`) as HTMLElement;
                                  
                                  if (currentField && prevField) {
                                    const currentFieldHeight = currentField.offsetHeight;
                                    const prevFieldHeight = prevField.offsetHeight;
                                    const gap = 16;
                                    const distanceUp = prevFieldHeight + gap;
                                    const distanceDown = currentFieldHeight + gap;

                                    setPendingSwap({
                                      fieldIds: [field.id, fields[currentIndex - 1].id],
                                      newFields,
                                      completedTransitions: 0
                                    });
                                    
                                    setAnimatingFields({
                                      [field.id]: { 
                                        transform: `translateY(${-distanceUp}px)`,
                                        class: 'animating'
                                      },
                                      [fields[currentIndex - 1].id]: { 
                                        transform: `translateY(${distanceDown}px)`,
                                        class: 'animating'
                                      }
                                    });
                                    setIsAnimating(true);
                                  }
                                }
                              }}
                            >
                              <FontAwesomeIcon icon={faChevronUp} />
                            </button>
                            <button
                              className={`order-button ${selectedDocument.items?.find(item => item.id === selectedItemId)?.fields?.findIndex(f => f.id === field.id) === (selectedDocument.items?.find(item => item.id === selectedItemId)?.fields?.length || 0) - 1 ? 'disabled' : ''}`}
                              onMouseEnter={() => setHoveredButtonId(`${field.id}-down`)}
                              onMouseLeave={() => setHoveredButtonId(null)}
                              onClick={async () => {
                                const docRef = doc(dbTools as Firestore, 'nbmtmform', selectedDocument.id);
                                const currentItem = selectedDocument.items?.find(item => item.id === selectedItemId);
                                const fields = currentItem?.fields || [];
                                const currentIndex = fields.findIndex(f => f.id === field.id);
                                
                                if (currentIndex < fields.length - 1) {
                                  setIsAnimating(true);
                                  
                                  const newFields = [...fields];
                                  [newFields[currentIndex], newFields[currentIndex + 1]] = 
                                    [newFields[currentIndex + 1], newFields[currentIndex]];
                                  
                                  const currentField = fieldsContainerRef.current?.querySelector(`[data-field-id="${field.id}"]`) as HTMLElement;
                                  const nextField = fieldsContainerRef.current?.querySelector(`[data-field-id="${fields[currentIndex + 1].id}"]`) as HTMLElement;
                                  
                                  if (currentField && nextField) {
                                    const currentFieldHeight = currentField.offsetHeight;
                                    const nextFieldHeight = nextField.offsetHeight;
                                    const gap = 16;
                                    const distanceDown = nextFieldHeight + gap;
                                    const distanceUp = currentFieldHeight + gap;

                                    setPendingSwap({
                                      fieldIds: [field.id, fields[currentIndex + 1].id],
                                      newFields,
                                      completedTransitions: 0
                                    });
                                    
                                    setAnimatingFields({
                                      [field.id]: { 
                                        transform: `translateY(${distanceDown}px)`,
                                        class: 'animating'
                                      },
                                      [fields[currentIndex + 1].id]: { 
                                        transform: `translateY(${-distanceUp}px)`,
                                        class: 'animating'
                                      }
                                    });
                                    setIsAnimating(true);
                                  }
                                }
                              }}
                            >
                              <FontAwesomeIcon icon={faChevronDown} />
                            </button>
                          </div>
                        </>
                      )}
                      {field.type === 'comment' ? (
                        viewMode === 'AUTHORING' ? (
                          <AutoSizingTextarea
                            value={field.value}
                            onChange={(value) => {
                              const docRef = doc(dbTools as Firestore, 'nbmtmform', selectedDocument.id);
                              const updatedItems = selectedDocument.items?.map(item =>
                                item.id === selectedItemId
                                  ? {
                                      ...item,
                                      fields: item.fields?.map(f =>
                                        f.id === field.id
                                          ? { ...f, value }
                                          : f
                                      ) || []
                                    }
                                  : item
                              ) || [];
                              updateDoc(docRef, { items: updatedItems }).then(() => {
                                triggerAutosave();
                              });
                              setDocuments(docs => docs.map(doc => 
                                doc.id === selectedDocument.id 
                                  ? { ...doc, items: updatedItems }
                                  : doc
                              ));
                            }}
                            placeholder="Comment..."
                            className="field-textarea label"
                          />
                        ) : (
                          <div className="field-comment">
                            {field.value}
                          </div>
                        )
                      ) : field.type === 'shortanswer' || field.type === 'longanswer' ? (
                        <>
                          {viewMode === 'AUTHORING' && (
                            <AutoSizingTextarea
                              value={field.value}
                              onChange={(value) => {
                                const docRef = doc(dbTools as Firestore, 'nbmtmform', selectedDocument.id);
                                const updatedItems = selectedDocument.items?.map(item =>
                                  item.id === selectedItemId
                                    ? {
                                        ...item,
                                        fields: item.fields?.map(f =>
                                          f.id === field.id
                                            ? { ...f, value }
                                            : f
                                        ) || []
                                      }
                                    : item
                                ) || [];
                                updateDoc(docRef, { items: updatedItems }).then(() => {
                                  triggerAutosave();
                                });
                                setDocuments(docs => docs.map(doc => 
                                  doc.id === selectedDocument.id 
                                    ? { ...doc, items: updatedItems }
                                    : doc
                                ));
                              }}
                              placeholder="Question..."
                              className="field-textarea label"
                            />
                          )}
                          {(viewMode === 'VIEWING' || viewMode === 'FILLINGOUT') && (
                            <div className="field-label">
                              {field.value}
                            </div>
                          )}
                          <div className="field-input-container">
                            {field.type === 'longanswer' ? (
                              viewMode === 'AUTHORING' ? (
                                field.inputValue ? (
                                  <AutoSizingTextarea
                                    value={field.inputValue}
                                    onChange={() => {}}
                                    readOnly
                                    className="field-textarea field-textarea-preview"
                                  />
                                ) : (
                                  <div className="field-textarea field-textarea-preview">
                                    Detailed answer
                                  </div>
                                )
                              ) : (
                                viewMode === 'FILLINGOUT' ? (
                                  <input
                                    type="text"
                                    value={field.inputValue || ''}
                                    onChange={(e) => {
                                      const docRef = doc(dbTools as Firestore, 'nbmtmform', selectedDocument.id);
                                      const updatedItems = selectedDocument.items?.map(item =>
                                        item.id === selectedItemId
                                          ? {
                                              ...item,
                                              fields: item.fields?.map(f =>
                                                f.id === field.id
                                                  ? { ...f, inputValue: e.target.value }
                                                  : f
                                              ) || []
                                            }
                                          : item
                                      ) || [];
                                      updateDoc(docRef, { items: updatedItems }).then(() => {
                                        triggerAutosave();
                                      });
                                      setDocuments(docs => docs.map(doc => 
                                        doc.id === selectedDocument.id 
                                          ? { ...doc, items: updatedItems }
                                          : doc
                                      ));
                                    }}
                                    className="field-input"
                                    placeholder="Enter text..."
                                    readOnly={viewMode as string === 'VIEWING' || viewMode as string === 'AUTHORING'}
                                  />
                                ) : (
                                  <AutoSizingTextarea
                                    value={field.inputValue || ''}
                                    onChange={(value) => {
                                      const docRef = doc(dbTools as Firestore, 'nbmtmform', selectedDocument.id);
                                      const updatedItems = selectedDocument.items?.map(item =>
                                        item.id === selectedItemId
                                          ? {
                                              ...item,
                                              fields: item.fields?.map(f =>
                                                f.id === field.id
                                                  ? { ...f, inputValue: value }
                                                  : f
                                              ) || []
                                            }
                                          : item
                                      ) || [];
                                      updateDoc(docRef, { items: updatedItems }).then(() => {
                                        triggerAutosave();
                                      });
                                      setDocuments(docs => docs.map(doc => 
                                        doc.id === selectedDocument.id 
                                          ? { ...doc, items: updatedItems }
                                          : doc
                                      ));
                                    }}
                                    className="field-textarea"
                                    placeholder="Enter text..."
                                    readOnly={viewMode as string === 'VIEWING' || viewMode as string === 'AUTHORING'}
                                  />
                                )
                              )
                            ) : (
                              viewMode === 'AUTHORING' ? (
                                field.inputValue ? (
                                  <input
                                    type="text"
                                    value={field.inputValue}
                                    readOnly
                                    className="field-input field-input-preview"
                                  />
                                ) : (
                                  <div className="field-input field-input-preview">
                                    Short answer
                                  </div>
                                )
                              ) : (
                                viewMode === 'FILLINGOUT' ? (
                                  <input
                                    type="text"
                                    value={field.inputValue || ''}
                                    onChange={(e) => {
                                      const docRef = doc(dbTools as Firestore, 'nbmtmform', selectedDocument.id);
                                      const updatedItems = selectedDocument.items?.map(item =>
                                        item.id === selectedItemId
                                          ? {
                                              ...item,
                                              fields: item.fields?.map(f =>
                                                f.id === field.id
                                                  ? { ...f, inputValue: e.target.value }
                                                  : f
                                              ) || []
                                            }
                                          : item
                                      ) || [];
                                      updateDoc(docRef, { items: updatedItems }).then(() => {
                                        triggerAutosave();
                                      });
                                      setDocuments(docs => docs.map(doc => 
                                        doc.id === selectedDocument.id 
                                          ? { ...doc, items: updatedItems }
                                          : doc
                                      ));
                                    }}
                                    className="field-input"
                                    placeholder="Enter text..."
                                    readOnly={viewMode as string === 'VIEWING' || viewMode as string === 'AUTHORING'}
                                  />
                                ) : (
                                  <AutoSizingTextarea
                                    value={field.inputValue || ''}
                                    onChange={(value) => {
                                      const docRef = doc(dbTools as Firestore, 'nbmtmform', selectedDocument.id);
                                      const updatedItems = selectedDocument.items?.map(item =>
                                        item.id === selectedItemId
                                          ? {
                                              ...item,
                                              fields: item.fields?.map(f =>
                                                f.id === field.id
                                                  ? { ...f, inputValue: value }
                                                  : f
                                              ) || []
                                            }
                                          : item
                                      ) || [];
                                      updateDoc(docRef, { items: updatedItems }).then(() => {
                                        triggerAutosave();
                                      });
                                      setDocuments(docs => docs.map(doc => 
                                        doc.id === selectedDocument.id 
                                          ? { ...doc, items: updatedItems }
                                          : doc
                                      ));
                                    }}
                                    className="field-textarea"
                                    placeholder="Enter text..."
                                    readOnly={viewMode as string === 'VIEWING' || viewMode as string === 'AUTHORING'}
                                  />
                                )
                              )
                            )}
                          </div>
                        </>
                      ) : field.type === 'options' ? (
                        <>
                          {viewMode === 'AUTHORING' && (
                            <AutoSizingTextarea
                              value={field.value}
                              onChange={(value) => {
                                const docRef = doc(dbTools as Firestore, 'nbmtmform', selectedDocument.id);
                                const updatedItems = selectedDocument.items?.map(item =>
                                  item.id === selectedItemId
                                    ? {
                                        ...item,
                                        fields: item.fields?.map(f =>
                                          f.id === field.id
                                            ? { ...f, value }
                                            : f
                                        ) || []
                                      }
                                    : item
                                ) || [];
                                updateDoc(docRef, { items: updatedItems }).then(() => {
                                  triggerAutosave();
                                });
                                setDocuments(docs => docs.map(doc => 
                                  doc.id === selectedDocument.id 
                                    ? { ...doc, items: updatedItems }
                                    : doc
                                ));
                              }}
                              placeholder="Question..."
                              className="field-textarea label"
                            />
                          )}
                          {(viewMode === 'VIEWING' || viewMode === 'FILLINGOUT') && (
                            <div className="field-label">
                              {field.value}
                            </div>
                          )}
                          <div className={`binary-options-container ${viewMode === 'AUTHORING' ? 'authoring' : ''}`}>
                            {(field.options || ['Yes', 'No', 'Not sure']).map((option, index) => (
                              <label 
                                key={index} 
                                className={`binary-option ${viewMode === 'AUTHORING' ? 'authoring' : ''} ${animatingOptions[`${field.id}-${index}`]?.class || ''}`}
                                data-option-id={`${field.id}-${index}`}
                                style={{
                                  transform: animatingOptions[`${field.id}-${index}`]?.transform || ''
                                }}
                                onTransitionEnd={() => {
                                  handleOptionTransitionEnd(field.id, index);
                                }}
                              >
                                <div
                                  onClick={() => {
                                    if (viewMode === 'FILLINGOUT') {
                                      const docRef = doc(dbTools as Firestore, 'nbmtmform', selectedDocument.id);
                                      const updatedItems = selectedDocument.items?.map(item =>
                                        item.id === selectedItemId
                                          ? {
                                              ...item,
                                              fields: item.fields?.map(f =>
                                                f.id === field.id
                                                  ? { 
                                                      ...f, 
                                                      selectedOption: field.selectedOption === option ? null : option
                                                    }
                                                  : f
                                              ) || []
                                            }
                                          : item
                                      ) || [];
                                      updateDoc(docRef, { items: updatedItems }).then(() => {
                                        triggerAutosave();
                                      });
                                      setDocuments(docs => docs.map(doc => 
                                        doc.id === selectedDocument.id 
                                          ? { ...doc, items: updatedItems }
                                          : doc
                                      ));
                                    }
                                  }}
                                  className={`binary-radio ${field.selectedOption === option ? 'selected' : ''}`}
                                />
                                {viewMode === 'AUTHORING' ? (
                                  <>
                                    <input
                                      type="text"
                                      value={option}
                                      onChange={(e) => {
                                        const docRef = doc(dbTools as Firestore, 'nbmtmform', selectedDocument.id);
                                        const updatedItems = selectedDocument.items?.map(item =>
                                          item.id === selectedItemId
                                            ? {
                                                ...item,
                                                fields: item.fields?.map(f =>
                                                  f.id === field.id
                                                    ? { 
                                                        ...f, 
                                                        options: (f.options || ['Yes', 'No', 'Not sure']).map((o, i) => 
                                                          i === index ? e.target.value : o
                                                        ),
                                                        selectedOption: f.selectedOption
                                                      }
                                                    : f
                                                  ) || []
                                              }
                                            : item
                                        ) || [];
                                        updateDoc(docRef, { items: updatedItems }).then(() => {
                                          triggerAutosave();
                                        });
                                        setDocuments(docs => docs.map(doc => 
                                          doc.id === selectedDocument.id 
                                            ? { ...doc, items: updatedItems }
                                            : doc
                                        ));
                                      }}
                                      className="field-input"
                                    />
                                    <div className="binary-option-controls">
                                      <button
                                        className={`order-button ${index === 0 ? 'disabled' : ''}`}
                                        onMouseEnter={() => setHoveredButtonId(`${field.id}-option-${index}-up`)}
                                        onMouseLeave={() => setHoveredButtonId(null)}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (index > 0) {
                                            const docRef = doc(dbTools as Firestore, 'nbmtmform', selectedDocument.id);
                                            const options = [...(field.options || ['Yes', 'No', 'Not sure'])];
                                            [options[index - 1], options[index]] = [options[index], options[index - 1]];
                                            
                                            const currentOption = document.querySelector(`[data-option-id="${field.id}-${index}"]`) as HTMLElement;
                                            const prevOption = document.querySelector(`[data-option-id="${field.id}-${index - 1}"]`) as HTMLElement;
                                            
                                            if (currentOption && prevOption) {
                                              const currentOptionHeight = currentOption.offsetHeight;
                                              const prevOptionHeight = prevOption.offsetHeight;
                                              const gap = 4;
                                              const distanceUp = prevOptionHeight + gap;
                                              const distanceDown = currentOptionHeight + gap;

                                              setPendingOptionSwap({
                                                fieldId: field.id,
                                                optionIndexes: [index, index - 1],
                                                newOptions: options,
                                                completedTransitions: 0
                                              });
                                              
                                              setAnimatingOptions({
                                                [`${field.id}-${index}`]: { 
                                                  transform: `translateY(${-distanceUp}px)`,
                                                  class: 'animating'
                                                },
                                                [`${field.id}-${index - 1}`]: { 
                                                  transform: `translateY(${distanceDown}px)`,
                                                  class: 'animating'
                                                }
                                              });
                                            }
                                          }
                                        }}
                                      >
                                        <FontAwesomeIcon icon={faChevronUp} />
                                      </button>
                                      <button
                                        className={`order-button ${index === (field.options?.length || 0) - 1 ? 'disabled' : ''}`}
                                        onMouseEnter={() => setHoveredButtonId(`${field.id}-option-${index}-down`)}
                                        onMouseLeave={() => setHoveredButtonId(null)}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (index < (field.options?.length || 0) - 1) {
                                            const docRef = doc(dbTools as Firestore, 'nbmtmform', selectedDocument.id);
                                            const options = [...(field.options || ['Yes', 'No', 'Not sure'])];
                                            [options[index], options[index + 1]] = [options[index + 1], options[index]];
                                            
                                            const currentOption = document.querySelector(`[data-option-id="${field.id}-${index}"]`) as HTMLElement;
                                            const nextOption = document.querySelector(`[data-option-id="${field.id}-${index + 1}"]`) as HTMLElement;
                                            
                                            if (currentOption && nextOption) {
                                              const currentOptionHeight = currentOption.offsetHeight;
                                              const nextOptionHeight = nextOption.offsetHeight;
                                              const gap = 4;
                                              const distanceDown = nextOptionHeight + gap;
                                              const distanceUp = currentOptionHeight + gap;

                                              setPendingOptionSwap({
                                                fieldId: field.id,
                                                optionIndexes: [index, index + 1],
                                                newOptions: options,
                                                completedTransitions: 0
                                              });
                                              
                                              setAnimatingOptions({
                                                [`${field.id}-${index}`]: { 
                                                  transform: `translateY(${distanceDown}px)`,
                                                  class: 'animating'
                                                },
                                                [`${field.id}-${index + 1}`]: { 
                                                  transform: `translateY(${-distanceUp}px)`,
                                                  class: 'animating'
                                                }
                                              });
                                            }
                                          }
                                        }}
                                      >
                                        <FontAwesomeIcon icon={faChevronDown} />
                                      </button>
                                      {(field.options || ['Yes', 'No', 'Not sure']).length > 1 && (
                                        <button
                                          className="binary-option-delete"
                                          onClick={() => {
                                            const docRef = doc(dbTools as Firestore, 'nbmtmform', selectedDocument.id);
                                            const updatedItems = selectedDocument.items?.map(item =>
                                              item.id === selectedItemId
                                                ? {
                                                    ...item,
                                                    fields: item.fields?.map(f =>
                                                      f.id === field.id
                                                        ? { 
                                                            ...f, 
                                                            options: (f.options || ['Yes', 'No', 'Not sure']).filter((_, i) => i !== index)
                                                          }
                                                        : f
                                                    ) || []
                                                  }
                                                : item
                                            ) || [];
                                            updateDoc(docRef, { items: updatedItems }).then(() => {
                                              triggerAutosave();
                                            });
                                            setDocuments(docs => docs.map(doc => 
                                              doc.id === selectedDocument.id 
                                                ? { ...doc, items: updatedItems }
                                                : doc
                                            ));
                                          }}
                                        >
                                          <FontAwesomeIcon icon={faClose} />
                                        </button>
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  <span className="binary-option-text">{option}</span>
                                )}
                              </label>
                            ))}
                          </div>
                        </>
                      ) : null}
                    </div>
                  ) : null
                ))}
              </div>
              {viewMode === 'AUTHORING' && selectedItemId && (
                <div className="add-field-container">
                  <select
                    value={fieldType}
                    onChange={(e) => {
                      if (e.target.value === 'comment' || e.target.value === 'shortanswer' || e.target.value === 'longanswer' || e.target.value === 'options') {
                        const docRef = doc(dbTools as Firestore, 'nbmtmform', selectedDocument.id);
                        const newField: NBMTMField = {
                          id: Date.now().toString(),
                          type: e.target.value,
                          value: '',
                          inputValue: '',
                          ...(e.target.value === 'options' ? { 
                            options: ['Yes', 'No', 'Not sure'],
                            selectedOption: null
                          } : {})
                        };
                        const updatedItems = selectedDocument.items?.map(item =>
                          item.id === selectedItemId
                            ? { 
                                ...item, 
                                fields: [...(item.fields || []), newField]
                              }
                            : item
                        ) || [];
                        updateDoc(docRef, { items: updatedItems }).then(() => {
                          triggerAutosave();
                        });
                        setDocuments(docs => docs.map(doc => 
                          doc.id === selectedDocument.id 
                            ? { ...doc, items: updatedItems }
                            : doc
                        ));
                        setFieldType('');
                      }
                    }}
                    className="nbmtm-secure-select"
                  >
                    <option value="" disabled>Add field</option>
                    <option value="comment">Comment</option>
                    <option value="shortanswer">Short Answer</option>
                    <option value="longanswer">Long Answer</option>
                    <option value="options">Options</option>
                  </select>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Secure;